// src/app/ClientLayout.tsx
"use client";
import { Suspense, useEffect } from "react";
import { Providers } from "./providers";
import LayoutContent from "@/components/LayoutContent";
import { usePathname } from "next/navigation";
import { useAuth } from "@clerk/nextjs";
import { initCrashDiagnostics } from "@/lib/crashDiagnostics";
import GAPageviewTracker from "@/components/analytics/GAPageviewTracker";

function InnerClientLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() || "/";

  // 2026-11-02：afterSignUp（ユーザー文書の作成・ようこそメール）は、Clerkが「ログイン済み」と
  // 確定してから呼ぶ。以前はページを開いた瞬間に（ログイン状態を見ずに）1回だけ呼んでいたため、
  // 未ログイン・ログイン直後はClerkのミドルウェアに404で弾かれ、新規登録の流れ（/sign-upから
  // ページ遷移で戻る）では、ClientLayoutが作り直されず二度と呼ばれなかった（test4でメール未着）。
  // ログイン直後はセッションがサーバーに届くまで404になることがあるため、数回までやり直す。
  const { isLoaded, isSignedIn, userId } = useAuth();
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !userId) return;
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const delays = [0, 2000, 5000];
    const attempt = (i: number) => {
      fetch("/api/user/afterSignUp", { method: "POST" })
        .then((res) => {
          if (cancelled || res.ok) return;
          if (i + 1 < delays.length) timers.push(setTimeout(() => attempt(i + 1), delays[i + 1]));
        })
        .catch(() => {
          if (cancelled) return;
          if (i + 1 < delays.length) timers.push(setTimeout(() => attempt(i + 1), delays[i + 1]));
        });
    };
    attempt(0);
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [isLoaded, isSignedIn, userId]);

  // 白画面インシデントの原因切り分け用（詳細は src/lib/crashDiagnostics.ts）
  useEffect(() => {
    initCrashDiagnostics();
  }, []);

  const isDemo = pathname.startsWith("/demo");
  const isClerkRoute =
    pathname.startsWith("/sign-in") ||
    pathname.startsWith("/sign-up") ||
    pathname.startsWith("/sso-callback");

  // GA4のページビュー計測はデモ・サインイン系ルートも含め全ルート対象。
  // useSearchParams を使うため Suspense 境界の内側（このコンポーネント）に置く。
  if (isDemo) {
    return (
      <>
        <GAPageviewTracker />
        {children}
      </>
    );
  }
  if (isClerkRoute) {
    return (
      <>
        <GAPageviewTracker />
        {children}
      </>
    );
  }

  return (
    <>
      <GAPageviewTracker />
      <Providers>
        <LayoutContent>{children}</LayoutContent>
      </Providers>
    </>
  );
}

export default function ClientLayout({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div />}>
      <InnerClientLayout>{children}</InnerClientLayout>
    </Suspense>
  );
}
